/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import compose from 'lodash/fp/compose';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import { Container, Row, Column } from '../../../styles/Grid';
import DotComponent from '../../ui/DotComponent';
import Spacer from '../../ui/Spacer';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { keyColors, colorsPalette } from '../../../styles/colors';
import {
  transformWildcardsToText,
  getWildcardData,
} from '../../../utils/wildcards';
import {
  textCss,
  titleCss,
  locationCss,
  locationLinkCss,
  locationsCss,
  containerMarginCss,
  containerPaddingCss,
} from './index.styles';

const defaultBackgroundColorKey = keyColors.brown10;

const defaultData = {
  AlternativeHeadline: {
    value: '',
  },
  AlternativeShortDescription: {
    value: '',
  },
  AlternativeLocations: {
    value: [],
  },
  BackgroundColor: {
    fields: {
      Value: {
        value: defaultBackgroundColorKey,
      },
    },
  },
};

export class AlternativeLocation extends SafePureComponent {
  constructor(props) {
    super(props);
    this.wildcards = getWildcardData(this.props.sitecoreContext);
  }

  /**
   * This function is tranforming the wildcard information to be used
   * inside the Headline and the Description of the component
   * @param {Object} fields containt the information of the current component
   * @return {React.Node} return the Headline and Description of the component
   */
  renderHeadlineAndDescription = fields => {
    const { t } = this.props;
    const contextFields = get(this.props, 'sitecoreContext.route.fields', {});
    const componentName = get(this.props, 'rendering.componentName');

    const defaultWildcardValuesToTransform = {
      componentName,
      contextFields,
      fields,
      t,
      wildcards: this.wildcards,
    };

    const AlternativeHeadline = {
      value: transformWildcardsToText({
        ...defaultWildcardValuesToTransform,
        text: get(fields, 'AlternativeHeadline.value', ''),
      }),
    };

    const AlternativeShortDescription = {
      value: transformWildcardsToText({
        ...defaultWildcardValuesToTransform,
        text: get(fields, 'AlternativeShortDescription.value', ''),
      }),
    };

    return (
      <Column cols={{ sm: 6, md: 8, lg: 5 }}>
        <h2 css={titleCss}>
          <Text field={AlternativeHeadline} />
          <DotComponent />
        </h2>
        <p css={textCss}>
          <Text field={AlternativeShortDescription} />
        </p>
      </Column>
    );
  };

  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);
    const isEditing = get(props, 'sitecoreContext.pageEditing', false);
    const shouldDisplay =
      fields.AlternativeLocations.value.length > 0 || isEditing;

    if (!shouldDisplay) {
      return null;
    }

    const BackgroundColor =
      fields.BackgroundColor || defaultData.BackgroundColor;
    const theme = get(
      BackgroundColor,
      'fields.Value.value',
      defaultBackgroundColorKey,
    );
    const isDarkTheme = theme === keyColors.black;
    const { white, black } = colorsPalette;

    const renderLocations = locations => {
      return locations.map((location, index, arr) => (
        <li key={index} css={locationCss}>
          <a css={locationLinkCss(theme)} href={location.Url}>
            {location.Name}
          </a>
          {index < arr.length - 1 ? <span>, </span> : <span>.</span>}
        </li>
      ));
    };

    return (
      <Spacer
        backgroundColor={theme}
        marginCss={containerMarginCss}
        paddingCss={containerPaddingCss}
      >
        <Container
          css={{
            color: isDarkTheme ? white : black,
          }}
        >
          <Row>
            {this.renderHeadlineAndDescription(fields)}
            <Column cols={{ sm: 6, md: 8, lg: 6 }} offsets={{ lg: 1 }}>
              <ul css={locationsCss}>
                {renderLocations(fields.AlternativeLocations.value)}
              </ul>
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

AlternativeLocation.displayName = 'AlternativeLocation';

AlternativeLocation.defaultProps = { fields: defaultData };

AlternativeLocation.propTypes = {
  AlternativeHeadline: PropTypes.shape({
    value: PropTypes.string,
  }),
  AlternativeShortDescription: PropTypes.shape({
    value: PropTypes.string,
  }),
  AlternativeLocations: PropTypes.shape({
    value: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.shape({
          value: PropTypes.string,
        }),
        url: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
    ),
  }),
  BackgroundColor: PropTypes.shape({
    fields: PropTypes.shape({
      Value: PropTypes.shape({
        value: PropTypes.string,
      }),
    }),
  }),
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
)(AlternativeLocation);
